<template>
  <div
    class="overflow-hidden"
    data-testid="reminderChip-container-root"
    @click.stop.prevent="handleReminderChipClick"
  >
    <v-chip
      data-testid="reminderChip-chip-main"
      close
      small
      class="cursor-pointer"
      :close-icon="'mdi-close-circle'"
      :color="isReminderExpired ? 'red lighten-2' : 'blue-grey lighten-3'"
      :style="{
        border: '1px solid',
        borderColor: `${$styleVariables.colorBlueGreyLighten2} !important`,
      }"
      @click:close="handleReminderClose"
    >
      <v-icon
        left
        small
        class="mr-2"
        :color="isReminderExpired ? 'red' : 'blue-grey darken-2'"
        >mdi-bell-ring-outline</v-icon
      >
      <v-icon
        v-if="isRepeatedReminder"
        left
        small
        class="mr-1"
        :color="isReminderExpired ? 'red' : 'blue-grey darken-2'"
        >mdi-repeat</v-icon
      >
      <span
        class="caption text-truncate"
        :class="{
          'red--text': isReminderExpired,
          'blue-grey--text text--darken-3': !isReminderExpired,
        }"
        style="max-width: 99%;"
      >
        {{ reminderMessage }}
      </span>
    </v-chip>
    <DeleteConfirmationModal
      :show="showDeleteModal"
      :title="$t('routines_reminder.confirm_deletion')"
      :text="$t('routines_reminder.are_you_sure_you_want_to_delete')"
      :isLoading="isLoadingReminder"
      @cancel="handleCancelReminderChip"
      @delete="$emit('deleteReminder', reminderId)"
    />
  </div>
</template>

<script>
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'

import { FREQUENCY_TYPE } from '@/lib/frequencies'
import moment from 'moment'
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import _ from 'lodash'

export default {
  data() {
    return {
      showDeleteModal: false,
      FREQUENCY_TYPE,
    }
  },
  mixins: [mixin, trackerMixin],

  props: {
    recurrence: Object,
    reminderId: String,
    isLoadingReminder: Boolean,
    isRepeatedReminder: Boolean,
  },
  components: {
    DeleteConfirmationModal,
  },
  computed: {
    localStartDate() {
      return moment.utc(this.recurrence.startDate).local()
    },

    isReminderExpired() {
      if (!this.recurrence?.until) return false

      return moment
        .utc(this.recurrence?.until)
        .local()
        .isBefore(moment())
    },

    byMonthDayValue() {
      if (_.isEmpty(this.recurrence?.byMonthDay)) {
        return this.localStartDate.date()
      }

      return this.recurrence?.byMonthDay?.[0]
    },

    monthlyReminderText() {
      if (this.recurrence?.byMonthDay?.length > 1) {
        return this.$t('routines_reminder.monthly', {
          date: this.$t('routines_reminder.last_day'),
        })
      }
      return this.$t('routines_reminder.monthly', {
        date: this.localStartDate
          .set('date', this.byMonthDayValue)
          .format('MMM DD, hh:mm A'),
      })
    },

    reminderMessage() {
      if (!this.isRepeatedReminder) return this.onceReminderText
      switch (this.recurrence?.frequency) {
        case FREQUENCY_TYPE.HOURLY:
          return this.recurrence?.interval === 1
            ? this.$t('routines_reminder.every_hour')
            : this.$t('routines_reminder.every_hours', {
                interval: this.recurrence?.interval,
              })
        case FREQUENCY_TYPE.DAILY:
          return this.dailyReminderText
        case FREQUENCY_TYPE.WEEKLY:
          return this.$t('routines_reminder.weekly', {
            date: this.localStartDate.format('hh:mm A'),
          })
        case FREQUENCY_TYPE.MONTHLY:
          return this.monthlyReminderText
        default:
          return this.$t('routines_reminder.yearly', {
            date: this.localStartDate.format('MMM DD, hh:mm A'),
          })
      }
    },

    isReminderExpiresTomorrow() {
      return this.localStartDate.isSame(
        moment()
          .add(1, 'day')
          .startOf('day'),
        'day'
      )
    },
    onceReminderText() {
      return this.isReminderExpiresTomorrow
        ? this.$t('routines_reminder.tomorrow', {
            date: this.localStartDate.format('hh:mm A'),
          })
        : this.localStartDate.isSame(moment(), 'day')
        ? this.$t('routines_reminder.today', {
            date: this.localStartDate.format('hh:mm A'),
          })
        : this.localStartDate.format('MMM DD, YYYY, hh:mm A')
    },

    dailyReminderText() {
      const hour = this.localStartDate.format('hh:mm A')
      return this.recurrence?.interval === 1
        ? this.$t('routines_reminder.every_day', { hour })
        : this.$t('routines_reminder.every_days', {
            interval: this.recurrence?.interval,
            hour,
          })
    },
  },
  methods: {
    syncCloseControlState() {
      const closeControl = this.$el?.querySelector('.v-chip__close')

      if (!closeControl) return

      closeControl.setAttribute(
        'data-testid',
        'reminderChip-button-deleteReminder'
      )
      closeControl.classList.remove('red--text', 'blue-grey--text')
      closeControl.classList.add(
        this.isReminderExpired ? 'red--text' : 'blue-grey--text'
      )
    },

    handleReminderChipClick(event) {
      if (event?.target?.closest('.v-chip__close')) return

      this.$emit('openReminderModal')
    },

    handleReminderClose() {
      this.showDeleteModal = true
    },

    handleCancelReminderChip() {
      this.showDeleteModal = false
      this.trackRoutineDefinitionReminder('reminder.delete', {
        process: 'cancel',
        entityType: 'routineDefinition',
        reminderId: this.reminderId,
      })
    },
  },

  mounted() {
    this.syncCloseControlState()
  },

  updated() {
    this.syncCloseControlState()
  },
}
</script>
